import React, { useEffect, useState } from 'react';
import { AlertTriangle, FileText, Download, Upload, CheckCircle2, TrendingUp, BarChart3, Activity, ClipboardList, Radar } from 'lucide-react';
import api from '../api/client';
import FormDialog from '../components/FormDialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { downloadAsFakePdf } from '../utils/export';
import { toast } from '../hooks/use-toast';
import QualiopiDashboard from '../components/QualiopiDashboard';
import QualiopiDocTemplates from '../components/QualiopiDocTemplates';
import RegistreEvenements from '../components/RegistreEvenements';
import RegistreVeille from '../components/RegistreVeille';

export default function Qualiopi() {
  const [qualiopiCriteria, setCriteria] = useState([]);
  const [qualiopiDocuments, setDocs] = useState([]);
  const [autoScore, setAutoScore] = useState(null);
  const [open, setOpen] = useState(false);
  const [evoOpen, setEvoOpen] = useState(false);
  const [tab, setTab] = useState(() => {
    const demande = new URLSearchParams(window.location.search).get('onglet');
    return ['overview', 'dashboard', 'templates', 'evenements', 'veille'].includes(demande)
      ? demande : 'overview';
  });

  const loadDocs = () => api.get('/qualiopi/documents').then(({ data }) => setDocs(data)).catch(() => {});

  useEffect(() => {
    api.get('/qualiopi/auto-score').then(({ data }) => {
      setCriteria(data.criteria);
      setAutoScore(data);
    }).catch(() => {
      // fallback to static
      api.get('/qualiopi/criteria').then(({ data }) => setCriteria(data)).catch(() => {});
    });
    loadDocs();
  }, []);

  const handleCreate = async (values) => {
    const payload = {
      ...values,
      size: values.size || '— Ko',
      date: values.date || new Date().toISOString().slice(0, 10),
      status: values.status || 'Valide',
    };
    const { data } = await api.post('/qualiopi-documents', payload);
    setDocs((prev) => [...prev, data]);
    toast({ title: 'Document importé', description: data.name });
  };

  const avg = autoScore?.global_score ?? null;
  const mesures = autoScore?.mesures ?? 0;
  const nonMesures = autoScore?.non_mesures || [];

  return (
    <div className="space-y-5">
      {/* Tabs */}
      <div className="flex items-center gap-1.5 border-b border-[#EFF0F6]">
        <button
          data-testid="qualiopi-tab-overview"
          onClick={() => setTab('overview')}
          className={`px-4 h-10 text-[13px] font-semibold border-b-2 transition-colors flex items-center gap-1.5 ${
            tab === 'overview' ? 'border-[#4F46E5] text-[#4F46E5]' : 'border-transparent text-[#6B6C85] hover:text-[#1A1B2E]'
          }`}
        >
          <CheckCircle2 className="w-4 h-4" /> Vue d'ensemble
        </button>
        <button
          data-testid="qualiopi-tab-dashboard"
          onClick={() => setTab('dashboard')}
          className={`px-4 h-10 text-[13px] font-semibold border-b-2 transition-colors flex items-center gap-1.5 ${
            tab === 'dashboard' ? 'border-[#4F46E5] text-[#4F46E5]' : 'border-transparent text-[#6B6C85] hover:text-[#1A1B2E]'
          }`}
        >
          <BarChart3 className="w-4 h-4" /> Dashboard global
        </button>
        <button
          data-testid="qualiopi-tab-evenements"
          onClick={() => setTab('evenements')}
          className={`px-4 h-10 text-[13px] font-semibold border-b-2 transition-colors flex items-center gap-1.5 ${
            tab === 'evenements' ? 'border-[#4F46E5] text-[#4F46E5]' : 'border-transparent text-[#6B6C85] hover:text-[#1A1B2E]'
          }`}
        >
          <ClipboardList className="w-4 h-4" /> Registre des événements
        </button>
        <button
          data-testid="qualiopi-tab-veille"
          onClick={() => setTab('veille')}
          className={`px-4 h-10 text-[13px] font-semibold border-b-2 transition-colors flex items-center gap-1.5 ${
            tab === 'veille' ? 'border-[#4F46E5] text-[#4F46E5]' : 'border-transparent text-[#6B6C85] hover:text-[#1A1B2E]'
          }`}
        >
          <Radar className="w-4 h-4" /> Registre de veille
        </button>
        <button
          data-testid="qualiopi-tab-templates"
          onClick={() => setTab('templates')}
          className={`px-4 h-10 text-[13px] font-semibold border-b-2 transition-colors flex items-center gap-1.5 ${
            tab === 'templates' ? 'border-[#4F46E5] text-[#4F46E5]' : 'border-transparent text-[#6B6C85] hover:text-[#1A1B2E]'
          }`}
        >
          <FileText className="w-4 h-4" /> Modèles de documents
        </button>
      </div>

      {tab === 'veille' ? (
        <RegistreVeille />
      ) : tab === 'evenements' ? (
        <RegistreEvenements />
      ) : tab === 'templates' ? (
        <QualiopiDocTemplates />
      ) : tab === 'dashboard' ? (
        <QualiopiDashboard />
      ) : (
        <>
      {/* Hero score */}
      <div className="soft-card p-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="flex items-center gap-5">
          <div className="relative w-24 h-24 shrink-0">
            <svg viewBox="0 0 120 120" className="-rotate-90">
              <circle cx="60" cy="60" r="50" fill="none" stroke="#F3F4F8" strokeWidth="12" />
              <circle cx="60" cy="60" r="50" fill="none" stroke="#14B8A6" strokeWidth="12" strokeLinecap="round"
                strokeDasharray={`${((avg ?? 0) / 100) * 314.16} 314.16`} />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-[26px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                {avg === null ? '—' : `${avg}%`}
              </span>
            </div>
          </div>
          <div>
            <div className={`inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2 py-0.5 rounded ${
              avg === null ? 'bg-[#F6F7FB] text-[#6B6C85]'
                : avg >= 90 ? 'bg-[#E6FBF7] text-[#0F766E]' : 'bg-[#FFFBEB] text-[#92400E]'
            }`}>
              {avg === null ? <AlertTriangle className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />}
              {avg === null ? 'Rien de mesuré' : avg >= 90 ? 'Tous les critères tenus' : 'Des critères à reprendre'}
            </div>
            <h2 className="text-[20px] font-bold text-[#1A1B2E] mt-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Score qualité global</h2>
            <p className="text-[13px] text-[#6B6C85] mt-0.5">
              Mesuré sur {mesures} critère{mesures > 1 ? 's' : ''} sur {autoScore?.total_count ?? 7}
              {nonMesures.length > 0 && ` · ${nonMesures.length} sans donnée à compter`}
            </p>
          </div>
        </div>
        <div className="lg:col-span-2 grid grid-cols-3 gap-4 border-l border-[#EFF0F6] lg:pl-6">
          <div>
            <div className="text-[12px] text-[#8E8FA5]">Indicateurs conformes</div>
            <div className="text-[22px] font-bold text-[#14B8A6] mt-0.5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{autoScore ? `${autoScore.compliant_count} / ${autoScore.total_count}` : '— / —'}</div>
            <div className="text-[11px] text-[#8E8FA5] mt-0.5">Parmi les critères mesurés</div>
          </div>
          <div>
            <div className="text-[12px] text-[#8E8FA5]">Taux d'émargement</div>
            <div className="text-[22px] font-bold text-[#F59E0B] mt-0.5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {autoScore?.signature_rate == null ? '—' : `${autoScore.signature_rate}%`}
            </div>
            <div className="text-[11px] text-[#8E8FA5] mt-0.5">{autoScore?.signature_detail || 'Séances passées'}</div>
          </div>
          <div>
            <div className="text-[12px] text-[#8E8FA5]">Satisfaction moyenne</div>
            <div className="text-[22px] font-bold text-[#3B82F6] mt-0.5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {autoScore?.satisfaction_avg == null ? '—' : `${autoScore.satisfaction_avg}%`}
            </div>
            <div className="text-[11px] text-[#8E8FA5] mt-0.5">
              {autoScore?.satisfaction_detail || 'Aucune copie notée'}
              {autoScore?.response_rate != null && ` · ${autoScore.response_rate}% de retours`}
            </div>
          </div>
        </div>
      </div>

      {/* 7 critères */}
      <div className="soft-card p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-[15.5px] font-semibold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Les 7 critères Qualiopi</h3>
            <p className="text-[12.5px] text-[#8E8FA5]">Scores détaillés par critère du référentiel</p>
          </div>
          <button onClick={() => setEvoOpen(true)} className="text-[12.5px] text-[#4F46E5] font-semibold hover:underline flex items-center gap-1">
            <TrendingUp className="w-4 h-4" /> Voir l'évolution
          </button>
        </div>
        <div className="space-y-2.5">
          {qualiopiCriteria.map((c) => (
            <div key={c.id} className="flex items-center gap-4 p-3 rounded-xl border border-[#F3F4F8] hover:bg-[#FAFAFE] transition-colors">
              <div className="w-10 h-10 rounded-lg bg-[#EEF2FF] flex items-center justify-center font-bold text-[13px] text-[#4F46E5] shrink-0">
                {c.id}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="text-[13.5px] font-semibold text-[#1A1B2E]">{c.name}</div>
                  <span className="text-[10.5px] text-[#8E8FA5]">{c.indicators} indicateurs</span>
                </div>
                {c.score === null ? (
                  /* Rien a compter : on le dit, et on dit le geste qui le
                     rendrait mesurable. Un trait a zero se lirait « vous etes
                     a zero », quand la verite est « personne n'a rien remis ». */
                  <div className="mt-1.5 text-[11.5px] text-[#8E8FA5]">
                    {c.geste || 'Rien à compter pour l’instant.'}
                  </div>
                ) : (
                  <>
                    <div className="mt-1.5 flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-[#F3F4F8] rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${c.score}%`, background: c.score >= 90 ? '#14B8A6' : '#F59E0B' }} />
                      </div>
                      <span className="text-[12.5px] font-bold text-[#1A1B2E] w-10 text-right">{c.score}%</span>
                    </div>
                    {/* De quoi le chiffre est fait : un score sans sa phrase
                        n'est qu'un nombre de plus. */}
                    {c.quoi && <div className="text-[11px] text-[#8E8FA5] mt-1">{c.quoi}</div>}
                  </>
                )}
              </div>
              <span className={`text-[11.5px] font-semibold px-2.5 py-1 rounded-md shrink-0 ${
                c.status === 'Conforme' ? 'bg-[#E6FBF7] text-[#0F766E]'
                  : c.status === 'Non mesuré' ? 'bg-[#F6F7FB] text-[#6B6C85]'
                  : 'bg-[#FEF3E7] text-[#B45309]'
              }`}>
                {c.status === 'Conforme' ? <CheckCircle2 className="w-3 h-3 inline mr-1" /> : <AlertTriangle className="w-3 h-3 inline mr-1" />}
                {c.status}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Documents */}
      <div className="soft-card p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-[15.5px] font-semibold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Documents qualité</h3>
            <p className="text-[12.5px] text-[#8E8FA5]">Preuves et justificatifs pour l'audit</p>
          </div>
          <button onClick={() => setOpen(true)} className="h-9 px-3.5 rounded-lg brand-gradient text-white text-[12.5px] font-semibold flex items-center gap-1.5 shadow-sm hover:opacity-95">
            <Upload className="w-4 h-4" /> Importer
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {qualiopiDocuments.map((d) => (
            <div key={d.id} className="flex items-center gap-3 p-3 rounded-xl border border-[#F3F4F8] hover:bg-[#FAFAFE] hover:border-[#C7D2FE] transition-all cursor-pointer">
              <div className="w-10 h-10 rounded-lg bg-[#EEF2FF] flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5 text-[#4F46E5]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-[#1A1B2E] truncate">{d.name}</div>
                <div className="text-[11.5px] text-[#8E8FA5]">{d.type} · {d.size} · {new Date(d.date).toLocaleDateString('fr-FR')}</div>
              </div>
              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${
                d.status === 'Valide' ? 'bg-[#E6FBF7] text-[#0F766E]' : 'bg-[#FEF3E7] text-[#B45309]'
              }`}>{d.status}</span>
              <button onClick={() => { downloadAsFakePdf(d.name.replace(/\s+/g, '-'), { 'Nom': d.name, 'Type': d.type, 'Taille': d.size, 'Date': d.date, 'Statut': d.status }); toast({ title: 'Téléchargement démarré', description: d.name }); }} className="w-8 h-8 rounded-lg hover:bg-[#EEF2FF] text-[#4F46E5] flex items-center justify-center">
                <Download className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      </div>
        </>
      )}

      <FormDialog
        open={open}
        onOpenChange={setOpen}
        title="Importer un document qualité"
        submitLabel="Importer"
        onSubmit={handleCreate}
        fields={[
          { name: 'name', label: 'Nom du document', required: true, full: true },
          { name: 'type', label: 'Type de fichier', type: 'select', options: ['PDF', 'DOCX', 'XLSX', 'PPTX'], required: true },
          { name: 'size', label: 'Taille', placeholder: 'Ex. 1.2 Mo' },
          { name: 'status', label: 'Statut', type: 'select', options: ['Valide', 'En cours'], defaultValue: 'Valide' },
          { name: 'date', label: 'Date', type: 'date' },
        ]}
      />

      <Dialog open={evoOpen} onOpenChange={setEvoOpen}>
        <DialogContent className="max-w-[620px] rounded-2xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Évolution du score Qualiopi</DialogTitle>
            <DialogDescription>Ce que le score a donné au fil du temps</DialogDescription>
          </DialogHeader>
          {/* Cette fenetre montrait cinq trimestres et « +10 points en 12 mois,
              progression continue » — des chiffres ecrits en dur, de Q1 2024 a
              Q1 2025. Le score n'est pas enregistre : il se recalcule a chaque
              ouverture, donc il n'y a aucun historique a tracer. Une courbe
              inventee a cote d'un score devenu honnete serait le dernier
              endroit ou l'on se mentirait. */}
          <div className="pt-4">
            <div className="rounded-xl bg-[#FAFAFE] border border-[#EFF0F6] p-5 text-center">
              <TrendingUp className="w-7 h-7 text-[#C7D2FE] mx-auto mb-2" />
              <div className="text-[13.5px] font-semibold text-[#1A1B2E]">
                Pas encore d&rsquo;historique
              </div>
              <div className="text-[12.5px] text-[#6B6C85] mt-1 max-w-sm mx-auto">
                Le score se recalcule à chaque ouverture, à partir de vos données du
                jour — rien n&rsquo;est conservé d&rsquo;un mois sur l&rsquo;autre.
                Pour voir une tendance, il faudra en garder une photo datée, chaque mois.
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
